import { OrganizationUserType } from '@mfw/common';
import { Outlet } from 'react-router-dom';

import { LegacyClientProfileRedirect } from '../../pages/Profiles/OrganizationClientProfile/Navigation/LegacyClientProfileRedirect';
import { LegacyBusinessFinanceRedirect } from '../../pages/BusinessFinancePages/LegacyBusinessFinanceRedirect';
import { InlineRouteErrorPage } from '../../pages/InlineRouteErrorPage';
import { crudChildrenWithPreview, crudChildren } from './helpers';
import { bookingEventTemplatesRoute } from './featureRoutes';
import { OrganizationGuard } from '../guards';
import { lazyRoute } from './lazyRoute';

const orgSectionChildren = [
  {
    // Reached directly from the sidebar, so no `handle.title` of its own —
    // same reasoning as `home`/`library` above. `new` and `:userId` opt out
    // of the app-wide trail too (`hideBreadcrumbs`) and render their own
    // "back to Clients" link instead.
    children: [
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () =>
          lazyRoute(
            '/Organizations/OrganizationUserInvitePage/OrganizationUserInvitePage',
          ),
        loader: () => ({ type: OrganizationUserType.CLIENT }),
        path: 'new',
      },
      {
        children: [
          {
            index: true,
            lazy: () =>
              lazyRoute(
                '/Profiles/OrganizationClientProfile/OrganizationClientProfile',
              ),
          },
        ],
        // filled in with the client's name by `useClientNameBreadcrumb`
        handle: { title: ':clientName', hideBreadcrumbs: true },
        loader: () => ({ type: OrganizationUserType.CLIENT }),
        path: ':userId',
      },
      {
        index: true,
        lazy: () =>
          lazyRoute(
            '/Organizations/OrganizationClientsPage/OrganizationClientsPage',
          ),
      },
    ],
    path: 'clients',
  },
  {
    children: [
      {
        handle: { title: 'add' },
        lazy: () => lazyRoute('/ClientGroups/ClientGroupFormPage'),
        path: 'new',
      },
      {
        handle: { title: 'edit' },
        lazy: () => lazyRoute('/ClientGroups/ClientGroupFormPage'),
        path: ':groupId',
      },
    ],
    handle: { title: 'client-groups', breadcrumbTo: '../clients' },
    path: 'client-groups',
  },
  {
    // Reached directly from the sidebar, so no `handle.title` of its own —
    // `new`/`:membershipId` opt out of the app-wide trail too and render
    // their own "back to Memberships" link.
    children: [
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: async () => lazyRoute('/MembershipsPages/MembershipPage'),
        path: 'new' as const,
      },
      {
        handle: { title: 'edit', hideBreadcrumbs: true },
        lazy: async () => lazyRoute('/MembershipsPages/MembershipPage'),
        path: ':membershipId' as const,
      },
      {
        index: true,
        lazy: async () => lazyRoute('/MembershipsPages/MembershipsPage'),
      },
    ],
    element: <Outlet />,
    path: 'memberships' as const,
  },
  // Behind the tenant's `sessions` switch (`featureRoutes.tsx`).
  bookingEventTemplatesRoute,
  {
    // Group-session statistics (attendance only). A sibling of the booking
    // routes rather than a child of one: it aggregates over every event in the
    // organization, so it belongs to none of them.
    // Reached directly from the sidebar with no drill-down of its own, so no
    // `handle.title` — see `home`/`schedule` below for the same reasoning.
    // Session statistics is now a tab of Performance.
    element: (
      <LegacyBusinessFinanceRedirect
        target="performance"
        defaultTab="sessions"
      />
    ),
    path: 'booking-stats',
  },
  {
    // The client profile's former second mount. One resource, one URL: the
    // canonical `/clients/:userId` is the only one with a breadcrumb trail and
    // a list to go back to, and `users` never had a page of its own for its
    // crumb to link to.
    children: [{ element: <LegacyClientProfileRedirect />, path: ':userId' }],
    path: 'users',
  },
  {
    // Pages opened from Organization Profile cards (not sidebar rows). A
    // pathless layout: its match pathname is `/organizations/:organizationId`,
    // so the trail reads Home / Organization Profile / <page> and the
    // sidebar's Organization Profile row stays highlighted. Their drill-down
    // pages opt out of the trail (`hideBreadcrumbs`) and render their own
    // back link.
    children: [
      {
        children: [
          {
            handle: { title: 'add', hideBreadcrumbs: true },
            lazy: () =>
              lazyRoute(
                '/Organizations/OrganizationTrainerInvitePage/OrganizationTrainerInvitePage',
              ),
            path: 'new',
          },
          {
            // F4b — post-accept trainer/admin profile form. Must stay declared
            // BEFORE the `:id` catch-all below (react-router ranks a static
            // segment over a dynamic one for the SAME position, but keeping the
            // literal route earlier mirrors the BE controller's route-order
            // rationale and avoids ambiguity).
            handle: { title: 'edit-profile', hideBreadcrumbs: true },
            lazy: () =>
              lazyRoute(
                '/Profiles/OrganizationMemberProfileEditPage/OrganizationMemberProfileEditPage',
              ),
            path: 'me/profile/edit',
          },
          {
            handle: { title: 'edit', hideBreadcrumbs: true },
            lazy: () =>
              lazyRoute(
                '/Profiles/OrganizationMemberProfile/OrganizationMemberProfile',
              ),
            path: ':id',
          },
          {
            index: true,
            lazy: () =>
              lazyRoute(
                '/Organizations/OrganizationMembersPage/OrganizationMembersPage',
              ),
          },
        ],
        handle: { title: 'organizationMembers' },
        path: 'members',
      },
      {
        children: [
          {
            children: [
              {
                children: [
                  {
                    index: true,
                    lazy: () => lazyRoute('/Categories/CategoryPage'),
                  },
                  {
                    lazy: () => lazyRoute('/Categories/CategoryPage'),
                    path: ':categoryId',
                  },
                  {
                    children: [
                      {
                        handle: { title: 'add', hideBreadcrumbs: true },
                        lazy: () => lazyRoute('/Categories/CategoryPage'),
                        path: 'new',
                      },
                      {
                        handle: { title: ':tagTitle', hideBreadcrumbs: true },
                        lazy: () => lazyRoute('/Categories/CategoryPage'),
                        path: ':id',
                      },
                    ],
                    // No page of its own: the crumb (and the tag editor's Back
                    // arrow) leads to the category that lists these tags.
                    handle: {
                      title: 'tags',
                      breadcrumbTo: '..',
                      hideBreadcrumbs: true,
                    },
                    path: 'tags',
                  },
                ],
              },
            ],
            // Every level under `categories` renders the SAME shared list/detail
            // view (`CategoryPage`, drawers over one `CategoryBrowser`), never a
            // hierarchy of separate pages — so it opts out of the app-wide trail
            // too, same as the bare `categories` index below. The browser's own
            // "‹ Categories" back affordance (mobile) and always-visible list
            // rail (desktop) stand in for it.
            handle: { title: ':categoryTitle', hideBreadcrumbs: true },
            path: ':categoryId',
          },
          { index: true, lazy: () => lazyRoute('/Categories/CategoryPage') },
        ],
        handle: { title: 'categories' },
        path: 'categories',
      },
    ],
    handle: { title: 'organizationProfile' },
  },
  {
    // Reached directly from the sidebar ("My Profile") with no drill-down of
    // its own, so no `handle.title` — see `home`/`schedule` below.
    children: [
      {
        index: true,
        lazy: () => lazyRoute('/Profiles/MyOrgProfile/MyOrgProfile'),
        path: ':userId',
      },
    ],
    path: 'me',
  },
  {
    // Reached directly from the sidebar, so no `handle.title` of its own —
    // its form page renders its own "back to Roles" link instead.
    children: crudChildren('/RolesPages/RolesPage', '/RolesPages/RolePage', {
      hideBreadcrumbs: true,
    }),
    path: 'roles',
  },
  {
    // The exercise edit route is only ever reached from the Library page's
    // "Exercises" tab (this bare list has no nav entry of its own). Its form
    // page (`new`/`:id`) renders its own "back to Library" link instead of
    // the app-wide breadcrumb trail, so it opts out via `hideBreadcrumbs`.
    children: crudChildren(
      '/ExercisesPages/ExercisesPages',
      '/ExercisesPages/ExercisePage',
      { hideBreadcrumbs: true },
    ),
    handle: { title: 'library', breadcrumbTo: '../library?tab=exercises' },
    path: 'exercises',
  },
  {
    // Same situation as `exercises` above: only ever reached from the
    // Library page's "Workouts" tab, so its form renders its own back link.
    children: crudChildrenWithPreview(
      '/WorkoutPages/WorkoutsPage',
      '/WorkoutPages/WorkoutPage',
      ':workoutId',
      { hideBreadcrumbs: true },
    ),
    handle: { title: 'library', breadcrumbTo: '../library?tab=workouts' },
    path: 'workouts',
  },
  {
    // Reached directly from the sidebar, so no `handle.title` — this page
    // shows no breadcrumb of its own. Its tabs (exercises/workouts/programs)
    // supply the trail back to it via `breadcrumbTo` on their edit routes.
    children: [
      { index: true, lazy: () => lazyRoute('/LibraryPage/LibraryPage') },
    ],
    path: 'library',
  },
  {
    // Same situation as `exercises` above: only ever reached from the
    // Library page's "Programs" tab, so its form renders its own back link.
    children: crudChildrenWithPreview(
      '/ProgramsPages/ProgramsPage',
      '/ProgramsPages/ProgramPage',
      ':programId',
      { hideBreadcrumbs: true },
    ),
    handle: { title: 'library', breadcrumbTo: '../library?tab=programs' },
    path: 'programs',
  },
  {
    // Same situation as `exercises` above: only ever reached from the
    // Nutrition page's "Groceries" tab, so its form renders its own back
    // link instead of the app-wide breadcrumb trail.
    children: [
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/GroceriesPages/GroceryPage'),
        path: 'new',
      },
      {
        handle: { title: 'edit', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/GroceriesPages/GroceryPage'),
        path: ':id',
      },
    ],
    handle: { title: 'library', breadcrumbTo: '../nutrition?tab=groceries' },
    path: 'groceries',
  },
  {
    // Same situation as `exercises` above: only ever reached from the
    // Nutrition page's "Meals" tab, so its form renders its own back link.
    children: crudChildren('/MealsPages/MealsPage', '/MealsPages/MealPage', {
      hideBreadcrumbs: true,
    }),
    handle: { title: 'library', breadcrumbTo: '../nutrition?tab=meals' },
    path: 'meals',
  },
  {
    // Same situation as `exercises` above: only ever reached from the
    // Nutrition page's "Nutrition Plans" tab, so its form renders its own
    // back link.
    children: crudChildren(
      '/NutritionPlansPages/NutritionPlansPage',
      '/NutritionPlansPages/NutritionPlanPage',
      { hideBreadcrumbs: true },
    ),
    handle: {
      title: 'library',
      breadcrumbTo: '../nutrition?tab=nutritionPlans',
    },
    path: 'nutrition-plans',
  },
  {
    // Same situation as `library` above: reached directly from the sidebar,
    // and its tabs (meals/nutrition-plans/groceries) are separate sibling
    // routes with their own `breadcrumbTo` back to it — so it carries no
    // `handle.title` of its own.
    children: [
      { index: true, lazy: () => lazyRoute('/NutritionPage/NutritionPage') },
    ],
    path: 'nutrition',
  },
  {
    // Reached directly from the sidebar, so no `handle.title` of its own —
    // same reasoning as `home`/`library` above. Every episode/module/course
    // `new`/`:id`/`:id/view` child opts out of the app-wide trail too
    // (`hideBreadcrumbs`) and renders its own "back to Education" link
    // instead.
    children: [
      { index: true, lazy: () => lazyRoute('/EducationPage/EducationPage') },
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/EpisodesPages/EpisodePage'),
        path: 'episodes/new',
      },
      {
        handle: { title: 'view', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/EpisodesPages/EpisodeViewerPage'),
        path: 'episodes/:id/view',
      },
      {
        handle: { title: 'edit', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/EpisodesPages/EpisodePage'),
        path: 'episodes/:id',
      },
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/ModulesPages/ModulePage'),
        path: 'modules/new',
      },
      {
        handle: { title: 'view', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/ModulesPages/ModuleViewerPage'),
        path: 'modules/:id/view',
      },
      {
        handle: { title: 'edit', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/ModulesPages/ModulePage'),
        path: 'modules/:id',
      },
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/CoursesPages/CoursePage'),
        path: 'courses/new',
      },
      {
        handle: { title: 'view', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/CoursesPages/CourseViewerPage'),
        path: 'courses/:id/view',
      },
      {
        handle: { title: 'edit', hideBreadcrumbs: true },
        lazy: () => lazyRoute('/CoursesPages/CoursePage'),
        path: 'courses/:id',
      },
    ],
    path: 'education',
  },
  {
    // Reached directly from the sidebar, so no `handle.title` of its own —
    // `new`/`:notificationId` opt out of the app-wide trail too and render
    // their own "back to Notification Center" link.
    children: [
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () =>
          lazyRoute(
            '/NotificationCenter/NotificationCenterTemplatePage/NotificationCenterTemplatePage',
          ),
        path: 'new',
      },
      {
        handle: { title: ':notificationTitle', hideBreadcrumbs: true },
        lazy: () =>
          lazyRoute(
            '/NotificationCenter/NotificationCenterTemplatePage/NotificationCenterTemplatePage',
          ),
        path: ':notificationId',
      },
      {
        index: true,
        lazy: () =>
          lazyRoute(
            '/NotificationCenter/NotificationCenterPage/NotificationCenterPage',
          ),
      },
    ],
    path: 'notification-center',
  },
  {
    // Reached directly from the sidebar, so no `handle.title` of its own —
    // `new`/`:questionnaireId` opt out of the app-wide trail too and render
    // their own "back to Questionnaires" link.
    children: [
      {
        handle: { title: 'add', hideBreadcrumbs: true },
        lazy: () =>
          lazyRoute('/Questionnaires/QuestionnairePage/QuestionnairePage'),
        path: 'new',
      },
      {
        handle: { title: ':questionnaireTitle', hideBreadcrumbs: true },
        lazy: () =>
          lazyRoute('/Questionnaires/QuestionnairePage/QuestionnairePage'),
        path: ':questionnaireId',
      },
      {
        index: true,
        lazy: () =>
          lazyRoute('/Questionnaires/QuestionnairesPage/QuestionnairesPage'),
      },
    ],
    path: 'questionnaires',
  },
  {
    // Only `finance/stats` ("Profits and losses") is a direct sidebar
    // destination — bare `finance` is reached only via legacy tab redirects
    // for platform actors, no separate nav entry. Neither carries
    // `handle.title`: same flat-leaf reasoning as `home`/`schedule` below.
    children: [
      {
        // "Profits & Losses" became "Profits & Expenses"; its measured tabs
        // moved to Performance and Marketplace Payouts.
        element: (
          <LegacyBusinessFinanceRedirect
            target="profits-expenses"
            tabs={{
              byTrainer: { target: 'performance', tab: 'revenue' },
              costs: { tab: 'expenses' },
              marketplace: { target: 'marketplace-payouts', tab: 'overview' },
              net: { tab: 'overview' },
              overview: { target: 'performance', tab: 'revenue' },
              subscriptions: { target: 'performance', tab: 'memberships' },
            }}
          />
        ),
        path: 'stats',
      },
      { index: true, lazy: () => lazyRoute('/FinancePages/FinancePage') },
    ],
    path: 'finance',
  },
  {
    // T15 — Payouts moved out of the billing page into its own route.
    // Reached directly from the sidebar with no drill-down of its own, so no
    // `handle.title` — see `home`/`schedule` below for the same reasoning.
    element: <LegacyBusinessFinanceRedirect target="marketplace-payouts" />,
    path: 'payouts',
  },
  // Business & Finance (PO, 25.09.2026): Performance · Profits & Expenses ·
  // Marketplace Payouts · MyFitWorld Platform Analytics. Flat sidebar
  // destinations, so no `handle.title` — same reasoning as `home` below.
  {
    lazy: () => lazyRoute('/BusinessFinancePages/PerformancePage'),
    path: 'performance',
  },
  {
    // MV2-577 Part 2 (3.4) — fees only, its own key (`trainer_earnings_read`);
    // the page gates itself (direct URL included).
    lazy: () =>
      lazyRoute('/BusinessFinancePages/TrainerEarnings/TrainerEarningsPage'),
    path: 'trainer-earnings',
  },
  {
    lazy: () => lazyRoute('/FinancePages/FinanceStatsPage'),
    path: 'profits-expenses',
  },
  {
    lazy: () => lazyRoute('/PayoutsPages/PayoutsPage'),
    path: 'marketplace-payouts',
  },
  {
    lazy: () => lazyRoute('/BusinessFinancePages/PlatformAnalyticsPage'),
    path: 'platform-analytics',
  },
  {
    // Every route below is a flat, single-page sidebar destination with no
    // list-and-detail hierarchy of its own — unlike `roles`/`clients`/etc.
    // above, there is no child crumb that would need this one's title text
    // or back-link, so it carries none: visiting it directly shows no
    // breadcrumb trail at all (a bare "Home / X" would only be pretending X
    // is nested under Home, which it isn't — they're sibling sidebar items).
    lazy: () => lazyRoute('/DeskPages/DeskPage'),
    path: 'desk',
  },
  {
    lazy: () => lazyRoute('/DeskPages/DeskSalesPage'),
    path: 'desk/sales',
  },
  {
    // Gated on `locker_keys_manage` by the page itself (the sidebar row is
    // not the guard), independent of the desk permissions above.
    lazy: () => lazyRoute('/LockerKeyPages/LockerKeysPage'),
    path: 'desk/keys',
  },
  {
    lazy: () => lazyRoute('/OrgHomePages/OrgHomePage'),
    path: 'home',
  },
  {
    lazy: () => lazyRoute('/StaffHomePages/StaffHomePage'),
    path: 'staff-home',
  },
  {
    lazy: () => lazyRoute('/OrgSchedulePages/OrgSchedulePage'),
    path: 'schedule',
  },
  {
    lazy: () => lazyRoute('/StaffSchedulePages/StaffSchedulePage'),
    path: 'staff-schedule',
  },
  {
    // "Business Performance" became "Performance".
    element: (
      <LegacyBusinessFinanceRedirect
        target="performance"
        tabs={{
          groups: { tab: 'clients' },
          overview: { tab: 'overview' },
          premium: { target: 'platform-analytics' },
          segments: { tab: 'clients' },
        }}
      />
    ),
    path: 'stats',
  },
  {
    lazy: () =>
      lazyRoute(
        '/Organizations/OrganizationBrandingPage/OrganizationBrandingPage',
      ),
    path: 'branding',
  },
  {
    handle: { title: 'organizationProfile' },
    index: true,
    lazy: () => lazyRoute('/Profiles/OrganizationProfile/OrganizationProfile'),
  },
];

export const organizationRoutes = {
  children: [
    {
      children: orgSectionChildren,
      // This layout has no page of its own beyond the `organization` index
      // route it wraps, so its pathname is identical to that index route's —
      // it must never count as an active nav ancestor for its other
      // (unrelated) siblings like `members`, `clients`, `schedule`, etc.
      handle: { navAncestorMatch: false },
      path: ':organizationId',
    },
  ],
  element: (
    <OrganizationGuard>
      <Outlet />
    </OrganizationGuard>
  ),
  /**
   * Every organization page bubbles its failures to HERE, not to `/`.
   *
   * `/` is the route whose element is the whole application shell — providers,
   * sidebar, header, sockets, chat client — and an `errorElement` REPLACES its
   * route's element. So a page that threw while rendering, or a `lazy()` chunk
   * that failed to arrive, used to take the entire app down and cost the user
   * a reload and a full re-connect. This mount is inside `AppLayout`'s
   * `<Outlet/>`, so the inner page draws in the content area and the shell
   * never notices.
   */
  errorElement: <InlineRouteErrorPage />,
  path: 'organizations',
};
